import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { Tab, TableCell } from 'semantic-ui-react';
import { RouteEntity, SegmentEntity } from 'Models/Entities';
import locationAndRoutesStore from 'Models/LocationAndRoutesStore';
import { Combobox } from 'Views/Components/Combobox/Combobox';
import { PricingStrategyKind } from './userPricingRows';

interface PricingRouteTabsProps {
	pricingStrategy: PricingStrategyKind;
	/** Renders one route's pane body; segments arrive sorted by stop order. */
	children: (route: RouteEntity, segmentsSorted: SegmentEntity[]) => React.ReactNode;
}

/**
 * The per-route chrome every pricing table shares: a route Combobox + Semantic Tab, with an
 * "All routes" pseudo-route prepended under Fixed pricing (ConsecutiveStops prices per segment,
 * so it starts on the first real route instead).
 *
 * Observer because locationAndRoutesStore populates asynchronously during app bootstrap — the
 * routes memo must recompute when it lands.
 */
export const PricingRouteTabs = observer(({ pricingStrategy, children }: PricingRouteTabsProps) => {
	const routes = useMemo(() => {
		const _routes = locationAndRoutesStore.routes.slice().sort((a, b) => a.name.localeCompare(b.name));
		if (pricingStrategy === 'ConsecutiveStops') {
			return _routes;
		}
		return [new RouteEntity({ name: 'All routes' }), ..._routes];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationAndRoutesStore.routes, pricingStrategy]);

	const [activeRoute, setActiveRoute] = React.useState<string | undefined>(() =>
		pricingStrategy === 'ConsecutiveStops' ? routes[0]?.id : undefined,
	);

	// ConsecutiveStops has no "All routes" pseudo-tab, so an unset selection must land on the
	// first real route — including when the strategy only resolves to ConsecutiveStops after
	// mount (the legacy tables forced this from inside their routes memo).
	React.useEffect(() => {
		if (pricingStrategy === 'ConsecutiveStops' && activeRoute === undefined) {
			setActiveRoute(routes[0]?.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pricingStrategy, routes]);

	return (
		<div className="pricing-table-container">
			<Combobox
				className="route-selector"
				options={routes.map(route => ({ display: route.name, value: route.id ?? 'default' }))}
				model={{
					activeRoute: activeRoute ?? 'default',
				}}
				modelProperty="activeRoute"
				label="Select route"
				onChange={(e, { value }) => {
					setActiveRoute(value?.toString());
				}}
			/>
			<Tab
				className="pricing-table-tab"
				activeIndex={routes.findIndex(x => x.id === (activeRoute === 'default' ? undefined : activeRoute))}
				panes={routes.map(route => ({
					menuItem: route.name,
					render: () => {
						const segmentsSorted = [...route.segments].sort(
							(a, b) => a.startStop.order - b.startStop.order,
						);
						return (
							<React.Fragment key={route.id ?? 'default'}>
								{children(route, segmentsSorted)}
							</React.Fragment>
						);
					},
				}))}
			/>
		</div>
	);
});

/** The per-segment header cells a ConsecutiveStops pricing table shows in place of "Cost". */
export function SegmentHeaderCells({ segments }: { segments: SegmentEntity[] }) {
	return (
		<>
			{segments.map((segment, index) => (
				<TableCell key={segment.id} textAlign="left">
					{`${index + 1}. `}
					{segment.startStop.location.shortName}→{segment.endStop.location.shortName}
				</TableCell>
			))}
		</>
	);
}
